/**
 * MescreenResults.tsx — the scores and results PDF of a mescreen order.
 *
 * Scores come from order metadata (`mescreenResults`, written by the server's
 * poll). The PDF is fetched on click: the server pulls it live from mescreen
 * every time, so there is no stored copy and no link to leak.
 */
import { useState } from 'react';
import { FileText } from 'lucide-react';
import { getMescreenResultsPdf, type MescreenTestSummary } from '@/services/integrations.service';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';

const LEVEL_STYLE: Record<string, string> = {
  low: 'bg-amber-100 text-amber-800',
  high: 'bg-amber-100 text-amber-800',
  average: 'bg-slate-100 text-slate-700',
  optimal: 'bg-emerald-100 text-emerald-800',
  'optimal +': 'bg-emerald-100 text-emerald-800',
};

/** Opens the PDF in a new tab. Exported for the orders table's inline button. */
export async function openMescreenPdf(patientId: number, orderId: number): Promise<void> {
  // Open synchronously so the browser does not block it as a pop-up, then
  // point it at the PDF once it arrives.
  const tab = window.open('', '_blank');
  try {
    const blob = await getMescreenResultsPdf(patientId, orderId);
    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    if (tab) tab.location.href = url;
    else window.location.href = url;
  } catch (e) {
    tab?.close();
    toast.error(getErrorMessage(e));
  }
}

export function MescreenResults({ patientId, orderId, meta }: {
  patientId: number;
  orderId: number;
  meta: Record<string, unknown>;
}) {
  const [opening, setOpening] = useState(false);
  const tests = (meta.mescreenResults as MescreenTestSummary[] | undefined) ?? [];
  const pdfReady = meta.mescreenPdfReady === true;
  const barcode = typeof meta.mescreenBarcode === 'string' ? meta.mescreenBarcode : '';

  if (tests.length === 0 && !barcode) return null;

  return (
    <div className="space-y-2">
      {barcode && <p className="text-xs text-slate-500">Kit barcode {barcode}</p>}

      {tests.length === 0 ? (
        <p className="text-sm text-slate-500">Waiting for the lab to process the sample.</p>
      ) : (
        tests.map((t) => (
          <div key={t.testId || t.testDate} className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{t.testDate ? `Tested ${new Date(t.testDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Test'}</span>
              {t.profile && <span className="font-medium text-slate-700">{t.profile}</span>}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {t.scores.map((s) => (
                <div key={s.group} className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
                  <p className="text-[11px] text-slate-500">{s.group}</p>
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-800">
                      {s.value ?? '—'}{s.unit ? ` ${s.unit}` : ''}
                      {s.max != null && <span className="font-normal text-slate-400"> / {s.max}</span>}
                    </span>
                    {s.level && (
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${LEVEL_STYLE[s.level] ?? 'bg-slate-100 text-slate-700'}`}>
                        {s.level}
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {pdfReady && (
        <button
          type="button"
          disabled={opening}
          onClick={async () => {
            setOpening(true);
            await openMescreenPdf(patientId, orderId);
            setOpening(false);
          }}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline disabled:opacity-60"
        >
          <FileText className="h-4 w-4" /> {opening ? 'Opening…' : 'Open results PDF'}
        </button>
      )}
    </div>
  );
}
